import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

// Key for storing OAuth accounts in localStorage
const OAUTH_ACCOUNTS_KEY = "oauth_accounts";

// Function to save OAuth account to stored list
const saveOAuthAccount = (userData) => {
  try {
    const existingAccounts = localStorage.getItem(OAUTH_ACCOUNTS_KEY);
    let accounts = existingAccounts ? JSON.parse(existingAccounts) : [];

    const existingIndex = accounts.findIndex(
      (acc) => acc.email === userData.email,
    );

    const accountData = {
      id: userData.id,
      email: userData.email,
      name: userData.name,
      tenantCode: userData.tenantCode,
      provider: userData.provider || "google",
      lastUsed: new Date().toISOString(),
      avatar: userData.picture || null,
      isSocialLogin: true,
    };

    if (existingIndex >= 0) {
      accounts[existingIndex] = {
        ...accounts[existingIndex],
        ...accountData,
        lastUsed: new Date().toISOString(),
      };
    } else {
      accounts.unshift(accountData);
    }

    accounts = accounts.slice(0, 5);
    localStorage.setItem(OAUTH_ACCOUNTS_KEY, JSON.stringify(accounts));
    console.log("✅ Saved OAuth account:", userData.email);
  } catch (error) {
    console.error("Error saving OAuth account:", error);
  }
};

const SocialCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) {
      console.log("SocialCallback - Already processed, skipping");
      return;
    }

    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const userDataParam = params.get("userData");
    const error = params.get("error");

    console.log("🔐 SocialCallback - Full URL:", window.location.href);
    console.log("🔐 SocialCallback - Token exists:", !!token);
    console.log("🔐 SocialCallback - UserData exists:", !!userDataParam);
    console.log("🔐 SocialCallback - Error:", error);

    // Handle error case from OAuth callback
    if (error) {
      hasProcessed.current = true;
      console.log("❌ SocialCallback - Error detected:", error);

      // Show appropriate toast message
      if (error === "account_deactivated") {
        toast.error(
          "Your account has been deactivated. Please contact support.",
          {
            id: "oauth-account-deactivated",
            duration: 6000,
          },
        );
      } else if (error === "social_login_failed") {
        toast.error("Social login failed. Please try again.", {
          id: "oauth-social-failed",
          duration: 4000,
        });
      } else if (error === "tenant_not_found") {
        toast.error("Store not found. Please check the URL.", {
          id: "oauth-tenant-not-found",
          duration: 4000,
        });
      } else if (error === "tenant_required") {
        toast.error(
          "Store context required. Please use the correct store URL.",
          {
            id: "oauth-tenant-required",
            duration: 4000,
          },
        );
      } else if (error === "server_error") {
        toast.error("Server error. Please try again later.", {
          id: "oauth-server-error",
          duration: 4000,
        });
      } else {
        toast.error(`Login failed: ${error.replace(/_/g, " ")}`, {
          id: "oauth-error",
          duration: 4000,
        });
      }

      // Redirect to login page after a short delay to show toast
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 200);
      return;
    }

    if (token && userDataParam) {
      try {
        const userData = JSON.parse(decodeURIComponent(userDataParam));
        console.log("✅ SocialCallback - User data received:", userData.email);
        console.log("🏪 Tenant:", userData.tenantCode);

        hasProcessed.current = true;

        const enhancedUserData = {
          ...userData,
          isSocialLogin: true,
          provider: userData.provider || "google",
        };

        saveOAuthAccount(enhancedUserData);

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(enhancedUserData));
        localStorage.setItem(
          "lastUser",
          JSON.stringify({
            email: enhancedUserData.email,
            name: enhancedUserData.name || "",
            provider: enhancedUserData.provider || "google",
          }),
        );

        if (enhancedUserData.email) {
          localStorage.setItem("lastUsedEmail", enhancedUserData.email);
        }

        if (enhancedUserData.tenantCode) {
          localStorage.setItem("tenantCode", enhancedUserData.tenantCode);
        }

        const tokenExpiry = new Date().getTime() + 7 * 24 * 60 * 60 * 1000;
        localStorage.setItem("tokenExpiry", tokenExpiry.toString());

        if (setUser && setToken) {
          setUser(enhancedUserData);
          setToken(token);
        }

        toast.success(
          `Welcome ${enhancedUserData.name || enhancedUserData.email}!`,
          {
            id: "login-success",
          },
        );

        setTimeout(() => {
          if (enhancedUserData.role === "Admin") {
            navigate("/admin", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
        }, 100);
      } catch (error) {
        console.error("❌ Error parsing user data:", error);
        toast.error("Login failed. Please try again.", { id: "login-error" });
        navigate("/login", { replace: true });
      }
    } else if (!token && !userDataParam && !error) {
      console.log("SocialCallback - No token, userData, or error found");
      toast.error("Invalid login response. Please try again.", {
        id: "login-error",
      });
      navigate("/login", { replace: true });
    }
  }, [location, navigate, setUser, setToken]);

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ textAlign: "center" }}>
        <div style={{ marginBottom: "20px" }}>
          <div className="spinner"></div>
        </div>
        <h2>Completing your login...</h2>
        <p style={{ color: "#666", marginTop: "10px" }}>
          Please wait while we redirect you to your account.
        </p>
      </div>
    </div>
  );
};

export default SocialCallback;
